import { Stack, Text, Paper, Divider } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState, useEffect } from "react";
import { TaxInfoForm } from "./TaxInfoForm";
import type { TaxOfficeData } from "../../Onboarding/api/taxOfficeApi";
import {
  fetchTaxOfficeData,
  submitTaxOfficeData,
} from "../../Onboarding/api/taxOfficeApi";
import SettingsHeader from "../SettingsHeader/SettingsHeader";
import UserPreferencesSection from "./UserPreferencesSection";
import { useAuthStore } from "@/utils/stores/authStore";
import { refreshIntercomUserContext } from "@/utils/intercom";

interface OneClickSettingsProps {
  onSaveSuccess?: () => void;
}

export default function OneClickSettings({
  onSaveSuccess,
}: OneClickSettingsProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState<TaxOfficeData | null>(null);
  const isOwner = useAuthStore((state) => state.isOwner());

  useEffect(() => {
    const loadTaxOfficeData = async () => {
      try {
        const data = await fetchTaxOfficeData();
        setFormData(data);
      } catch (error) {
        console.error("Failed to load tax office information", error);
        notifications.show({
          title: "Error",
          message: "Failed to load tax office information",
          color: "red",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadTaxOfficeData();
  }, []);

  const handleSubmit = async (values: TaxOfficeData) => {
    try {
      await submitTaxOfficeData(values);
      setFormData(values);

      // Refresh Intercom with updated settings (tax software, intake system)
      // This ensures support sees current information
      refreshIntercomUserContext("settings").catch((error) => {
        console.error("[OneClickSettings] Failed to refresh Intercom:", error);
        // Don't show error to user - this is a background operation
      });

      // Call the onSaveSuccess callback if provided
      if (onSaveSuccess) {
        onSaveSuccess();
      }

      return true;
    } catch (error) {
      notifications.show({
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to update tax information. Please try again.",
        color: "red",
      });
      return false;
    }
  };

  if (isLoading) {
    return (
      <Paper p="md">
        <Text>Loading tax office information...</Text>
      </Paper>
    );
  }

  // Only show export preferences for TaxWise users (not TaxSlayer)
  const showExportPreferences =
    formData?.softwareType?.toLowerCase() !== "taxslayer";

  return (
    <Paper p="xl" radius="xl">
      <Stack gap={0}>
        <SettingsHeader
          title="One-Click Return Settings"
          order={3}
          description="This information is needed for One-Click Return and auto-fills your tax software for quicker e-filing. Changes are saved automatically."
        />

        <TaxInfoForm
          initialValues={formData || {}}
          onSubmit={handleSubmit}
          showSubmitButton={false}
          isOwner={isOwner}
        />

        {showExportPreferences && (
          <>
            <Divider my="xl" />
            <UserPreferencesSection />
          </>
        )}
      </Stack>
    </Paper>
  );
}
